const loadedSources = new Set<string>();
const pendingSources = new Map<string, Promise<void>>();
const retainedImages = new Map<string, HTMLImageElement>();
const sourceListeners = new Map<
  string,
  Set<(status: "loaded" | "error") => void>
>();

const MAX_RETAINED_IMAGES = 200;

const retainImage = (src: string, image: HTMLImageElement) => {
  retainedImages.delete(src);
  retainedImages.set(src, image);

  while (retainedImages.size > MAX_RETAINED_IMAGES) {
    const oldestSource = retainedImages.keys().next().value as
      | string
      | undefined;
    if (!oldestSource) break;
    retainedImages.delete(oldestSource);
  }
};

const notifySourceListeners = (
  src: string,
  status: "loaded" | "error"
) => {
  sourceListeners.get(src)?.forEach((listener) => listener(status));
};

export const isImageSourceLoaded = (src?: string) =>
  Boolean(src && loadedSources.has(src));

export const markImageSourceLoaded = (
  src: string,
  image?: HTMLImageElement
) => {
  loadedSources.add(src);
  if (image) retainImage(src, image);
  notifySourceListeners(src, "loaded");
};

export const forgetImageSource = (src: string) => {
  loadedSources.delete(src);
  retainedImages.delete(src);
  notifySourceListeners(src, "error");
};

export const isImageSourceRetained = (src?: string) =>
  Boolean(src && retainedImages.has(src));

export const subscribeToImageSource = (
  src: string,
  listener: (status: "loaded" | "error") => void
) => {
  const listeners = sourceListeners.get(src) ?? new Set();
  listeners.add(listener);
  sourceListeners.set(src, listeners);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) sourceListeners.delete(src);
  };
};

const preloadImageSource = (src: string) => {
  if (loadedSources.has(src) && retainedImages.has(src)) {
    return Promise.resolve();
  }

  const pending = pendingSources.get(src);
  if (pending) return pending;

  if (typeof Image === "undefined") return Promise.resolve();

  const request = new Promise<void>((resolve) => {
    const image = new Image();
    let settled = false;
    const finish = (loaded: boolean) => {
      if (settled) return;
      settled = true;
      pendingSources.delete(src);
      image.onload = null;
      image.onerror = null;

      if (loaded) markImageSourceLoaded(src, image);
      else forgetImageSource(src);

      resolve();
    };

    image.onload = () => finish(true);
    image.onerror = () => finish(false);
    image.src = src;

    if (image.complete) {
      finish(image.naturalWidth > 0);
      return;
    }

    image.decode?.().then(() => finish(true)).catch(() => undefined);
  });

  pendingSources.set(src, request);
  return request;
};

export const preloadImageSources = async (sources: Array<string | undefined>) => {
  const uniqueSources = Array.from(
    new Set(sources.filter((source): source is string => Boolean(source)))
  );

  await Promise.all(uniqueSources.map(preloadImageSource));
};

export const resetImageSourceCache = () => {
  loadedSources.clear();
  pendingSources.clear();
  retainedImages.clear();
  sourceListeners.clear();
};
